/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AiProfilesFormView.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    AI Profiles form component with cascading selections.
    Provider → Type → Model → Voices (for TTS)
    
    Refactored to use modular hooks for better maintainability.

 🔧 Notes:
    - Implements cascading selection: Provider → Type → Model → Voices
    - Supports connection testing and real-time validation
    - Handles multiple AI providers and models by type
    - Provides comprehensive error handling and user feedback
    - Supports both create and edit modes
    - Integrates with FormBox for consistent UI

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import {
  Bot,
  Search,
  Volume2,
  Copy,
  Cpu,
  Plus
} from 'lucide-react';

import { useNotifications } from '../../contexts/NotificationContext';
import { useDebugMode } from '../../hooks/useDebugMode';
import { useI18n } from '../../hooks/useI18n';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { FormBox } from '../../components/ui/FormBox';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Select from '../../components/ui/select';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { navigateToAiProfileDuplicate } from './aiProfilesActions';

// Helper to wrap labels with tooltips consistently
const TooltipLabel = ({ htmlFor, label, tooltip, required }: { htmlFor: string; label: string; tooltip?: string; required?: boolean }) => {
  if (!tooltip) {
    return (
      <Label htmlFor={htmlFor} className="text-left" required={required}>
        {label}
      </Label>
    );
  }
  return (
    <SimpleTooltip content={tooltip}>
      <Label htmlFor={htmlFor} className="text-left" required={required}>
        {label}
      </Label>
    </SimpleTooltip>
  );
};

// Import modular components
import {
  BasicConfigurationSection,
  StatusSection,
  AzureVoiceSelection,
  AzureVoiceConfiguration,
  AzureVoiceCascadingSelection,
  OpenAIVoiceConfiguration,
  GoogleVoiceConfiguration
} from './components';

// Import hooks
import { useProfileData } from './hooks/useProfileData';
import { useProfileForm } from './hooks/useProfileForm';
import { useProfileSubmit } from './hooks/useProfileSubmit';
import { useQuickList } from './hooks/useQuickList';
import { useProviderData } from './hooks/useProviderData';
import { useVoiceState } from './hooks/useVoiceState';
import { useVoiceHelpers } from './hooks/useVoiceHelpers';

// Import shared types
import type { AiProfile } from './api/aiProfilesApi';
import type { AiProvider } from './types';

import { AiProfileTTSModal } from './components/AiProfileTTSModal';

export default function AiProfilesFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo, notifications, removeNotification } = useNotifications();
  const { isDebugModeActive } = useDebugMode();

  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new' && !duplicateId);
  const mode = id === 'new' || duplicateId ? 'create' : 'edit';

  // TTS Modal state (kept here due to complexity)
  const [testingTTS, setTestingTTS] = useState(false);
  const [downloadingProductionAudio, setDownloadingProductionAudio] = useState(false);
  const [testText, setTestText] = useState('');
  const [showTTSModal, setShowTTSModal] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [ttsError, setTtsError] = useState<string | null>(null);
  const [modalTestConfig, setModalTestConfig] = useState({
    pitchValue: 0,
    volumeValue: 100,
    rateValue: 1,
    personality: '',
    voice: ''
  });
  const [originalModalConfig, setOriginalModalConfig] = useState({
    pitchValue: 0,
    volumeValue: 100,
    rateValue: 1,
    personality: '',
    voice: ''
  });

  // Use hooks
  const { formData, setFormData, baseline, setBaseline, isLoading } = useProfileData(id, duplicateId, resetParam);

  // Create a basic handleInputChange for useProviderData
  const handleInputChangeBasic = useCallback((field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, [setFormData]);

  // Get provider data first to have selectedProvider available
  const {
    providers,
    selectedProvider,
    availableTypes,
    availableModels,
    availableVoices,
    availableVoicesData,
    loadingTypes,
    loadingModels,
    loadingVoices
  } = useProviderData(formData, isEditMode, handleInputChangeBasic);

  const { errors, hasChanges, validateForm, handleInputChange } = useProfileForm(
    formData,
    baseline,
    mode,
    setFormData,
    selectedProvider
  );
  const {
    isSaving,
    isDeleting,
    showDeleteModal,
    setShowDeleteModal,
    submitProfile,
    handleCancel,
    handleDelete
  } = useProfileSubmit(
    formData,
    baseline,
    hasChanges,
    validateForm,
    mode,
    id,
    setFormData,
    setBaseline
  );

  // Create internal save handler that wraps submitProfile
  const handleSaveInternal = useCallback(async (): Promise<AiProfile | undefined> => {
    return await submitProfile();
  }, [submitProfile]);

  // Integrate useSaveWithAction
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/admin/ai-integration/profiles',
    newPath: '/admin/ai-integration/profiles/new',
    mode,
    getEditPath: (savedData: AiProfile) => {
      if (savedData.id) {
        return `/admin/ai-integration/profiles/${savedData.id}`;
      }
      return '/admin/ai-integration/profiles';
    }
  });
  const {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
    handleItemSelect,
    currentItemId
  } = useQuickList(mode, id, duplicateId);
  const {
    selectedVoiceData,
    setSelectedVoiceData,
    voiceSearch,
    setVoiceSearch,
    isVoiceDropdownOpen,
    setIsVoiceDropdownOpen,
    voiceDropdownRef,
    azureAudioConfig,
    setAzureAudioConfig,
    voiceStyles,
    setVoiceStyles,
    handleVoiceStyleChange,
    handleVoiceStyleSet,
    handleAzureConfigChange
  } = useVoiceState(formData, selectedProvider, availableVoicesData, handleInputChange);
  const { getPitchLabel, getVolumeLabel, getRateLabel } = useVoiceHelpers();

  // TTS Modal handlers (kept here due to complexity)
  const handleOpenTTSModal = useCallback(() => {
    if (!selectedVoiceData || !formData.provider_id) {
      showError(t('aiProfiles.errors.selectVoiceFirst'));
      return;
    }
    const parseToNumber = (val: string, type: 'pitch' | 'volume' | 'rate'): number => {
      if (!val) return type === 'pitch' ? 0 : type === 'volume' ? 100 : 1;
      const numMatch = val.match(/(-?\d+\.?\d*)/);
      if (numMatch) return parseFloat(numMatch[1]);
      return type === 'pitch' ? 0 : type === 'volume' ? 100 : 1;
    };
    const initialConfig = {
      pitchValue: parseToNumber(azureAudioConfig.pitch, 'pitch'),
      volumeValue: parseToNumber(azureAudioConfig.volume, 'volume'),
      rateValue: parseToNumber(azureAudioConfig.rate, 'rate'),
      personality: voiceStyles.personality || '',
      voice: selectedVoiceData?.ShortName || 'alloy'
    };
    setModalTestConfig(initialConfig);
    setOriginalModalConfig(initialConfig);
    setShowTTSModal(true);
    setAudioUrl(null);
    setTtsError(null);
  }, [selectedVoiceData, formData.provider_id, azureAudioConfig, voiceStyles, showError, t]);

  const handleOpenAITTSModal = useCallback(() => {
    if (!formData.provider_id) {
      showError(t('aiProfiles.errors.selectProviderFirst'));
      return;
    }
    let voiceConfig: Record<string, unknown> = {};
    try {
      if (formData.voice_config) {
        voiceConfig = typeof formData.voice_config === 'string'
          ? JSON.parse(formData.voice_config)
          : formData.voice_config;
      }
    } catch (error) {
      console.error('Error parsing voice_config:', error);
    }
    const model = voiceConfig.model || formData.model || 'tts-1';
    const voice = voiceConfig.voice || 'alloy';
    if (!model || !voice) {
      showError(t('aiProfiles.errors.configureVoiceFirst'));
      return;
    }
    const initialConfig = {
      pitchValue: 0,
      volumeValue: 100,
      rateValue: parseFloat(String(voiceConfig.speed || '')) || 1.0,
      personality: String(voiceConfig.instructions || ''),
      voice: String(voice)
    };
    setModalTestConfig(initialConfig);
    setOriginalModalConfig(initialConfig);
    setShowTTSModal(true);
    setAudioUrl(null);
    setTtsError(null);
  }, [formData, showError, t]);

  const handleGenerateTTS = useCallback(async () => {
    try {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      setTestingTTS(true);
      setTtsError(null);

      const textToSpeak = testText.trim() || t('aiProfiles.fields.testVoice.tooltip');
      let requestBody: Record<string, unknown>;

      if (selectedProvider?.provider === 'azure' && selectedVoiceData) {
        const voiceName = selectedVoiceData.Name || selectedVoiceData.ShortName;
        let pitchRel = 'medium';
        if (modalTestConfig.pitchValue > 10) pitchRel = 'high';
        else if (modalTestConfig.pitchValue < -10) pitchRel = 'low';
        let volumeRel = 'medium';
        if (modalTestConfig.volumeValue > 80) volumeRel = 'loud';
        else if (modalTestConfig.volumeValue < 20) volumeRel = 'soft';
        let rateRel = 'medium';
        if (modalTestConfig.rateValue > 1.2) rateRel = 'fast';
        else if (modalTestConfig.rateValue < 0.8) rateRel = 'slow';

        const ssmlContent = `<speak xmlns="http://www.w3.org/2001/10/synthesis" version="1.0" xml:lang="${selectedVoiceData.Locale}">
<voice name="${voiceName}">
<prosody pitch="${pitchRel}" volume="${volumeRel}" rate="${rateRel}">
${textToSpeak}
</prosody>
</voice>
</speak>`;

        requestBody = {
          provider_id: formData.provider_id,
          ssml: ssmlContent,
          output_format: 'audio-24khz-48kbitrate-mono-mp3'
        };
      } else if (selectedProvider?.provider === 'openai') {
        let voiceConfig: Record<string, unknown> = {};
        try {
          if (formData.voice_config) {
            voiceConfig = typeof formData.voice_config === 'string'
              ? JSON.parse(formData.voice_config)
              : formData.voice_config;
          }
        } catch (error) {
          console.error('Error parsing voice_config:', error);
          voiceConfig = {};
        }

        requestBody = {
          provider_id: formData.provider_id,
          text: textToSpeak,
          model: voiceConfig.model || formData.model || 'tts-1',
          voice: modalTestConfig.voice || voiceConfig.voice || 'alloy',
          response_format: voiceConfig.responseFormat || 'mp3',
          speed: modalTestConfig.rateValue || 1.0
        };
      } else if (selectedProvider?.provider === 'google') {
        // Google TTS - get voice config from formData
        let voiceConfig: Record<string, unknown> = {};
        try {
          if (formData.voice_config) {
            voiceConfig = typeof formData.voice_config === 'string'
              ? JSON.parse(formData.voice_config)
              : formData.voice_config;
          }
        } catch (error) {
          console.error('Error parsing voice_config:', error);
        }

        requestBody = {
          provider_id: formData.provider_id,
          text: textToSpeak,
          voice_config: voiceConfig
        };
      } else {
        throw new Error('Unsupported provider for TTS testing');
      }

      const response = await fetch(buildApiUrl('/api/ai/profiles/test-tts'), {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const audioBlob = await response.blob();
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        showSuccess(t('aiProfiles.messages.ttsSuccess'));
      } else {
        const errorData = await response.json();
        setTtsError(errorData.message || t('aiProfiles.errors.ttsFailed'));
        showError(errorData.message || t('aiProfiles.errors.ttsFailed'));
      }
    } catch (error) {
      const errorMsg = error.message || t('aiProfiles.errors.ttsFailed');
      setTtsError(errorMsg);
      showError(errorMsg);
    } finally {
      setTestingTTS(false);
    }
  }, [testText, selectedProvider, selectedVoiceData, formData, modalTestConfig, showSuccess, showError, t]);

  const handleDownloadProductionAudio = useCallback(async () => {
    if (!formData.provider_id) return;

    setDownloadingProductionAudio(true);
    setTtsError(null);

    try {
      const textToSpeak = testText.trim() || t('aiProfiles.fields.testVoice.tooltip');
      let requestBody: Record<string, unknown>;
      let filename: string;

      if (selectedProvider?.provider === 'azure' && selectedVoiceData) {
        const voiceName = selectedVoiceData.Name;
        const ssmlContent = `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${selectedVoiceData.Locale}"><voice name="${voiceName}">${textToSpeak}</voice></speak>`;
        requestBody = {
          provider_id: formData.provider_id,
          ssml: ssmlContent,
          production: true
        };
        filename = `voice-test-${selectedVoiceData.ShortName}-${Date.now()}.wav`;
      } else if (selectedProvider?.provider === 'openai') {
        let voiceConfig: Record<string, unknown> = {};
        try {
          if (formData.voice_config) {
            voiceConfig = typeof formData.voice_config === 'string'
              ? JSON.parse(formData.voice_config)
              : formData.voice_config;
          }
        } catch (error) {
          console.error('Error parsing voice_config:', error);
          voiceConfig = {};
        }

        requestBody = {
          provider_id: formData.provider_id,
          text: textToSpeak,
          model: voiceConfig.model || formData.model || 'tts-1',
          voice: modalTestConfig.voice || voiceConfig.voice || 'alloy',
          response_format: 'wav',
          speed: modalTestConfig.rateValue || 1.0,
          production: true
        };
        filename = `voice-test-openai-${Date.now()}.wav`;
      } else {
        showError(t('aiProfiles.errors.unsupportedProvider'));
        setDownloadingProductionAudio(false);
        return;
      }

      const response = await fetch(buildApiUrl('/api/ai/profiles/test-tts'), {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const audioBlob = await response.blob();
        const url = URL.createObjectURL(audioBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showSuccess(t('aiProfiles.messages.productionAudioDownloaded'));
      } else {
        const errorData = await response.json();
        setTtsError(errorData.message || t('aiProfiles.errors.ttsFailed'));
        showError(errorData.message || t('aiProfiles.errors.ttsFailed'));
      }
    } catch (error) {
      setTtsError(error instanceof Error ? error.message : t('aiProfiles.errors.ttsFailed'));
      showError(error instanceof Error ? error.message : t('aiProfiles.errors.ttsFailed'));
    } finally {
      setDownloadingProductionAudio(false);
    }
  }, [testText, formData, selectedProvider, selectedVoiceData, modalTestConfig, showSuccess, showError, t]);

  const handleCloseTTSModal = useCallback(() => {
    setShowTTSModal(false);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setTtsError(null);
  }, [audioUrl]);

  const handleApplyModalChanges = useCallback(() => {
    setAzureAudioConfig({
      ...azureAudioConfig,
      pitch: `${modalTestConfig.pitchValue}%`,
      volume: `${modalTestConfig.volumeValue}%`,
      rate: `${modalTestConfig.rateValue}x`
    });

    if (modalTestConfig.personality) {
      setVoiceStyles(prev => ({ ...prev, personality: modalTestConfig.personality }));
    }

    setOriginalModalConfig({
      pitchValue: modalTestConfig.pitchValue,
      volumeValue: modalTestConfig.volumeValue,
      rateValue: modalTestConfig.rateValue,
      personality: modalTestConfig.personality,
      voice: modalTestConfig.voice
    });

    showSuccess(t('aiProfiles.messages.voiceConfigUpdated'));
  }, [modalTestConfig, azureAudioConfig, setVoiceStyles, showSuccess, t]);

  const hasModalChanges = useCallback(() => {
    return (
      modalTestConfig.pitchValue !== originalModalConfig.pitchValue ||
      modalTestConfig.volumeValue !== originalModalConfig.volumeValue ||
      modalTestConfig.rateValue !== originalModalConfig.rateValue ||
      modalTestConfig.personality !== originalModalConfig.personality ||
      modalTestConfig.voice !== originalModalConfig.voice
    );
  }, [modalTestConfig, originalModalConfig]);

  const handleList = useCallback(() => {
    navigate('/admin/ai-integration/profiles');
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('aiProfiles.messages.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6"><ModuleFormHeader
      icon={<Bot className="h-4 w-4 text-primary" />}
      title={isEditMode ? `${t('aiProfiles.actions.edit')} - ${formData.model || formData.type || ''}` : t('aiProfiles.actions.create')}
      backButtonText={t('aiProfiles.actions.list')}
      onBack={handleList}
      actions={[
        {
          icon: <Search className="h-5 w-5 text-muted-foreground" />,
          tooltip: t('aiProfiles.quickList', 'Quick List'),
          onClick: () => setShowQuickList(true)
        },
        ...(isEditMode && id && id !== 'new' && !duplicateId
          ? [{
            icon: <Copy className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('common.duplicate'),
            onClick: () => navigateToAiProfileDuplicate(navigate, id)
          }]
          : []),
        {
          icon: <Plus className="h-5 w-5 text-muted-foreground" />,
          tooltip: t('aiProfiles.actions.add'),
          onClick: () => navigate('/admin/ai-integration/profiles/new')
        }
      ]}
    />

      <div className="mt-8">
        <form onSubmit={(e) => { e.preventDefault(); handleSaveWithAction('save'); }} className="space-y-6">
          {/* Basic Configuration Section */}
          <BasicConfigurationSection
            formData={formData}
            providers={providers}
            availableTypes={availableTypes}
            availableModels={availableModels}
            loadingTypes={loadingTypes}
            loadingModels={loadingModels}
            selectedProvider={selectedProvider}
            errors={errors}
            isEditMode={isEditMode}
            onInputChange={handleInputChange}
          />

          {/* Voice Selection - Only for Azure TTS (New Cascading Selection) */}
          {selectedProvider?.provider === 'azure' && (
            <AzureVoiceCascadingSelection
              formData={formData}
              availableVoicesData={availableVoicesData}
              loadingVoices={loadingVoices}
              onInputChange={handleInputChange}
              selectedVoiceData={selectedVoiceData}
              onVoiceSelect={(voice) => {
                setSelectedVoiceData(voice);
                if (voice) {
                  handleInputChange('voice_model', voice.ShortName);
                  handleInputChange('model', voice.ShortName);
                }
                setVoiceStyles({});
              }}
              voiceStyles={voiceStyles}
              onVoiceStyleSet={handleVoiceStyleSet}
            />
          )}

          {/* OpenAI Voice Configuration - Only for OpenAI TTS */}
          {selectedProvider?.provider === 'openai' && formData.type === 'tts' && (
            <FormBox title={t('aiProfiles.sections.voiceConfiguration')} icon={<Volume2 />}>
              <OpenAIVoiceConfiguration
                formData={formData}
                onInputChange={handleInputChange}
                selectedProvider={selectedProvider}
                onTestVoice={handleOpenAITTSModal}
              />
            </FormBox>
          )}

          {/* Google Voice Configuration - Only for Google TTS */}
          {selectedProvider?.provider === 'google' && formData.type === 'tts' && availableModels.length > 0 && (
            <GoogleVoiceConfiguration
              formData={formData}
              availableVoices={availableModels}
              onInputChange={handleInputChange}
              onTestVoice={() => {
                // Open the TTS test modal for Google
                setShowTTSModal(true);
                setAudioUrl(null);
                setTtsError(null);
              }}
            />
          )}

          {/* Advanced Configuration - Hide for Azure TTS, OpenAI TTS, and Google TTS */}
          {selectedProvider?.provider !== 'azure' && !(selectedProvider?.provider === 'openai' && formData.type === 'tts') && !(selectedProvider?.provider === 'google' && formData.type === 'tts') && (
            <FormBox title={t('aiProfiles.sections.advancedConfiguration')} icon={<Cpu />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                {/* Model-specific configuration fields for other providers TTS */}
                {formData.model && formData.type === 'tts' && selectedProvider?.provider !== 'azure' && selectedProvider?.provider !== 'openai' && (
                  <>
                    <TooltipLabel
                      htmlFor="voice_model"
                      label={t('aiProfiles.fields.voiceModel.label')}
                      tooltip={t('aiProfiles.fields.voiceModel.tooltip')}
                    />
                    <div className="col-span-1">
                      {availableVoices.length > 0 ? (
                        <Select
                          value={formData.voice_model || ''}
                          onValueChange={(value) => handleInputChange('voice_model', value)}
                          options={availableVoices.map(voice => ({
                            value: voice,
                            label: voice
                          }))}
                          placeholder={loadingVoices ? t('aiProfiles.messages.loadingVoices') : t('aiProfiles.fields.voiceModel.placeholder')}
                          disabled={loadingVoices}
                        />
                      ) : (
                        <Input
                          id="voice_model"
                          type="text"
                          value={formData.voice_model || ''}
                          onChange={(e) => handleInputChange('voice_model', e.target.value)}
                          placeholder={t('aiProfiles.fields.voiceModel.placeholder')}
                        />
                      )}
                    </div>
                    {errors.voice_model && <span className="col-span-2 text-sm text-red-500">{errors.voice_model}</span>}
                  </>
                )}

                {/* Temperature for chat/completion models */}
                {formData.model && ['general', 'agent_realtime'].includes(formData.type) && (
                  <>
                    <TooltipLabel
                      htmlFor="temperature"
                      label={t('aiProfiles.labels.temperature')}
                      tooltip={t('aiProfiles.fields.temperature.tooltip')}
                    />
                    <Input
                      id="temperature"
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={formData.temperature || ''}
                      onChange={(e) => handleInputChange('temperature', parseFloat(e.target.value))}
                      placeholder="0.7"
                    />
                    {errors.temperature && <span className="col-span-2 text-sm text-red-500">{errors.temperature}</span>}

                    <TooltipLabel
                      htmlFor="max_tokens"
                      label={t('aiProfiles.labels.maxTokens')}
                      tooltip={t('aiProfiles.fields.maxTokens.tooltip')}
                    />
                    <Input
                      id="max_tokens"
                      type="number"
                      min="1"
                      value={formData.max_tokens || ''}
                      onChange={(e) => handleInputChange('max_tokens', parseInt(e.target.value))}
                      placeholder="1000"
                    />
                    {errors.max_tokens && <span className="col-span-2 text-sm text-red-500">{errors.max_tokens}</span>}
                  </>
                )}


                {/* Timeout and Retry */}
                <TooltipLabel
                  htmlFor="timeout_ms"
                  label={t('aiProfiles.labels.timeout')}
                  tooltip={t('aiProfiles.fields.timeoutMs.tooltip')}
                />
                <Input
                  id="timeout_ms"
                  type="number"
                  min="1000"
                  max="60000"
                  step="1000"
                  value={formData.timeout_ms || ''}
                  onChange={(e) => handleInputChange('timeout_ms', parseInt(e.target.value))}
                  placeholder="30000"
                />
                {errors.timeout_ms && <span className="col-span-2 text-sm text-red-500">{errors.timeout_ms}</span>}

                <TooltipLabel
                  htmlFor="retry_attempts"
                  label={t('aiProfiles.labels.retryAttempts')}
                  tooltip={t('aiProfiles.fields.retryAttempts.tooltip')}
                />
                <Input
                  id="retry_attempts"
                  type="number"
                  min="0"
                  max="5"
                  value={formData.retry_attempts || ''}
                  onChange={(e) => handleInputChange('retry_attempts', parseInt(e.target.value))}
                  placeholder="3"
                />
                {errors.retry_attempts && <span className="col-span-2 text-sm text-red-500">{errors.retry_attempts}</span>}
              </div>
            </FormBox>
          )}


          {/* Debug Mode */}
          {isDebugModeActive && (
            <div className="mt-8 p-4 bg-muted rounded-lg">
              <h3 className="text-lg font-semibold mb-2">{t('aiProfiles.labels.debugInfo')}</h3>
              <pre className="text-sm overflow-auto">
                {JSON.stringify({ formData, errors, providers, availableTypes, availableModels, availableVoices }, null, 2)}
              </pre>
            </div>
          )}
        </form>
      </div>

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isEditMode={isEditMode}
        saveText={isSaving ? t('common.loading') : t('common.save')}
        cancelText={t('aiProfiles.actions.cancel')}
        deleteText={t('common.delete') || 'Delete'}
        saveDisabled={isSaving}
        isLoading={isSaving}
        hasChanges={hasChanges}
        useSaveDropdown={true}
        showSave={true}
      />

            <AiProfileTTSModal
        isOpen={showTTSModal}
        selectedProvider={selectedProvider}
        selectedVoiceData={selectedVoiceData}
        formData={formData}
        testText={testText}
        setTestText={setTestText}
        testingTTS={testingTTS}
        ttsError={ttsError}
        setTtsError={setTtsError}
        audioUrl={audioUrl}
        downloadingProductionAudio={downloadingProductionAudio}
        modalTestConfig={modalTestConfig}
        hasModalChanges={hasModalChanges}
        onClose={handleCloseTTSModal}
        onGenerateTTS={handleGenerateTTS}
        onApplyChanges={handleApplyModalChanges}
        onDownloadProduction={handleDownloadProductionAudio}
        t={t}
      />

            <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('aiProfiles.quickList', 'Quick List - Select Profile')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={handleItemSelect}
        currentItemId={currentItemId}
        renderItem={(profile, isCurrent) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{profile.profile_name || t('common.unknown')}</p>
              {profile.provider_name && (
                <p className="text-sm text-muted-foreground">{profile.provider_name}</p>
              )}
              {profile.model && (
                <p className="text-xs text-muted-foreground">{profile.model}</p>
              )}
            </div>
            {isCurrent && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                {t('aiProfiles.current', 'Current')}
              </span>
            )}
          </div>
        )}
        searchPlaceholder={t('aiProfiles.quickListSearchPlaceholder', 'Search by name, provider or model')}
        emptyMessage={t('aiProfiles.quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('aiProfiles.quickListEmpty', 'No profiles found')}
        headerIcon={<Bot className="h-5 w-5" />}
        minSearchChars={0}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          await handleDelete();
          navigate('/admin/ai-integration/profiles');
        }}
        title={t('aiProfiles.deleteConfirmTitle') || 'Delete AI Profile'}
        message={t('aiProfiles.deleteConfirmMessage') || `Are you sure you want to delete this AI profile? This action cannot be undone.`}
        confirmText={t('aiProfiles.deleteConfirm') || t('common.delete') || 'Delete'}
        cancelText={t('common.cancel') || 'Cancel'}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
